/**
 * Coloration syntaxique des blocs de code (design system, BlocDeCode) : grammaires Lezer
 * chargées à la demande, jetons ramenés aux six classes du design system.
 */
import { highlightTree, tagHighlighter, tags as t } from '@lezer/highlight';
import type { Parser } from '@lezer/common';

interface Langage {
  libelle: string;
  charger: () => Promise<Parser>;
}

const LANGAGES: Record<string, Langage> = {
  python: { libelle: 'Python', charger: async () => (await import('@lezer/python')).parser },
  javascript: {
    libelle: 'JavaScript',
    charger: async () => (await import('@lezer/javascript')).parser,
  },
  typescript: {
    libelle: 'TypeScript',
    charger: async () => (await import('@lezer/javascript')).parser.configure({ dialect: 'ts' }),
  },
  jsx: {
    libelle: 'JSX',
    charger: async () => (await import('@lezer/javascript')).parser.configure({ dialect: 'jsx' }),
  },
  tsx: {
    libelle: 'TSX',
    charger: async () =>
      (await import('@lezer/javascript')).parser.configure({ dialect: 'ts jsx' }),
  },
  json: { libelle: 'JSON', charger: async () => (await import('@lezer/json')).parser },
  css: { libelle: 'CSS', charger: async () => (await import('@lezer/css')).parser },
  html: { libelle: 'HTML', charger: async () => (await import('@lezer/html')).parser },
  xml: { libelle: 'XML', charger: async () => (await import('@lezer/xml')).parser },
  sql: {
    libelle: 'SQL',
    charger: async () => (await import('@codemirror/lang-sql')).StandardSQL.language.parser,
  },
  c: { libelle: 'C', charger: async () => (await import('@lezer/cpp')).parser },
  cpp: { libelle: 'C++', charger: async () => (await import('@lezer/cpp')).parser },
  java: { libelle: 'Java', charger: async () => (await import('@lezer/java')).parser },
  rust: { libelle: 'Rust', charger: async () => (await import('@lezer/rust')).parser },
  php: {
    libelle: 'PHP',
    charger: async () => (await import('@lezer/php')).parser.configure({ top: 'Program' }),
  },
  go: { libelle: 'Go', charger: async () => (await import('@lezer/go')).parser },
  bash: {
    libelle: 'Bash',
    charger: async () => {
      const [{ StreamLanguage }, { shell }] = await Promise.all([
        import('@codemirror/language'),
        import('@codemirror/legacy-modes/mode/shell'),
      ]);
      return StreamLanguage.define(shell).parser;
    },
  },
};

const ALIAS: Record<string, string> = {
  py: 'python',
  js: 'javascript',
  mjs: 'javascript',
  ts: 'typescript',
  'c++': 'cpp',
  cc: 'cpp',
  h: 'c',
  hpp: 'cpp',
  sh: 'bash',
  shell: 'bash',
  zsh: 'bash',
  rs: 'rust',
  golang: 'go',
  htm: 'html',
  svg: 'xml',
};

function langage(mot: string | undefined): Langage | null {
  if (!mot) return null;
  const nom = mot.trim().toLowerCase();
  return LANGAGES[ALIAS[nom] ?? nom] ?? null;
}

/** Libellé affiché d'un langage connu (« Python »), ou `null`. */
export function libelleLangage(mot: string | undefined): string | null {
  return langage(mot)?.libelle ?? null;
}

export type ClasseJeton = 'mot-cle' | 'chaine' | 'nombre' | 'commentaire' | 'type' | 'fonction';

const surligneur = tagHighlighter([
  {
    tag: [
      t.keyword,
      t.modifier,
      t.self,
      t.null,
      t.atom,
      t.bool,
      t.operatorKeyword,
      t.controlKeyword,
      t.definitionKeyword,
      t.moduleKeyword,
    ],
    class: 'mot-cle',
  },
  {
    tag: [t.string, t.special(t.string), t.regexp, t.character, t.docString, t.attributeValue],
    class: 'chaine',
  },
  { tag: [t.number, t.integer, t.float], class: 'nombre' },
  { tag: [t.comment, t.lineComment, t.blockComment, t.docComment, t.meta], class: 'commentaire' },
  { tag: [t.typeName, t.className, t.namespace, t.standard(t.typeName), t.tagName], class: 'type' },
  {
    tag: [
      t.function(t.variableName),
      t.function(t.propertyName),
      t.function(t.definition(t.variableName)),
      t.macroName,
      t.attributeName,
    ],
    class: 'fonction',
  },
]);

/** Une ligne colorée : morceaux de texte, avec leur classe éventuelle. */
export type LigneColoree = { texte: string; classe: ClasseJeton | null }[];

const cache = new Map<string, Promise<Parser | null>>();

/** Colore le code ; `null` si le langage est absent ou inconnu. */
export async function colorer(
  code: string,
  mot: string | undefined,
): Promise<LigneColoree[] | null> {
  const l = langage(mot);
  if (l === null) return null;
  let promesse = cache.get(l.libelle);
  if (promesse === undefined) {
    promesse = l.charger().catch(() => null);
    cache.set(l.libelle, promesse);
  }
  const parser = await promesse;
  if (parser === null) return null;
  const lignes: LigneColoree[] = [[]];
  const ajouter = (texte: string, classe: ClasseJeton | null) => {
    const morceaux = texte.split('\n');
    morceaux.forEach((morceau, i) => {
      if (i > 0) lignes.push([]);
      if (morceau !== '') lignes.at(-1)!.push({ texte: morceau, classe });
    });
  };
  const arbre = parser.parse(code);
  let position = 0;
  highlightTree(arbre, surligneur, (de, a, classes) => {
    if (de > position) ajouter(code.slice(position, de), null);
    ajouter(code.slice(de, a), (classes.split(' ')[0] as ClasseJeton) || null);
    position = a;
  });
  if (position < code.length) ajouter(code.slice(position), null);
  return lignes;
}
